import React, { createRef, useCallback, useState } from "react";
import Dropzone, { DropzoneRef } from "react-dropzone";
import { useFormState } from "react-final-form";
import clsx from "clsx";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import { NotifyMode } from "../../modules/notify/NotifyModeEnum";
import { useNotify } from "../../modules/notify/useNotify";
import InfoIcon from "../InfoIcon";
import { UploadFileContent } from "./UploadFileContent";
import { PreviewFile } from "./PreviewFile";
import { useStyles } from "./useStyles";
import { IUploadApplication } from "./types";

const errorMessageBeautifier = (fileRejections, maxSize) => {
  fileRejections.map((file) => {
    const index = file.errors.findIndex(
      (error) => error.code === "file-too-large",
    );

    if (index >= 0 && maxSize) {
      const currentMessage = file.errors[index].message;

      file.errors[index].message = currentMessage.slice(0, 20) + maxSize + "MB";
    }
  });
};

function UploadFile({
  maxSize,
  infoText,
  titleText,
  subText,
  required = false,
  removeFile,
  addNewFiles,
  maxFiles = 1,
  uploadedFiles,
  allowedFileTypes,
  isViewMode = false,
  name = "image",
}: IUploadApplication) {
  const classes = useStyles({
    isViewMode,
  });
  const [loading, setLoading] = useState(false);
  const snackNotify = useNotify();
  const dropzoneRef = createRef<DropzoneRef>();
  const formState = useFormState();
  const helperText = required ? formState.submitErrors?.[name] : undefined;

  const browse = () => {
    dropzoneRef.current?.open();
  };

  const onDropHandler = useCallback(
    async (acceptedFiles, fileRejections) => {
      if (uploadedFiles.length + acceptedFiles.length > maxFiles) {
        snackNotify("Error", "Too many files!", NotifyMode.ERROR);

        return;
      }

      if (fileRejections.length) {
        errorMessageBeautifier(fileRejections, maxSize);
        const fileRejectionErrorsArray = fileRejections.map(
          (file) => file.errors[0].message,
        );
        const filteredRejectionErrors = Array.from(
          new Set(fileRejectionErrorsArray),
        ).join("\n");
        snackNotify("Error", filteredRejectionErrors, NotifyMode.ERROR);

        return;
      }

      if (acceptedFiles.length) {
        setLoading(true);
        await addNewFiles!(acceptedFiles);
        setLoading(false);
      }
    },
    [uploadedFiles.length, maxFiles, snackNotify, maxSize, addNewFiles],
  );

  return (
    <div className={classes.root}>
      {!isViewMode && titleText && (
        <div className={classes.label}>
          {titleText}{" "}
          {required && (
            <Typography
              className={clsx(classes.label, helperText && classes.hasError)}
              component={"span"}
              variant={"subtitle2"}
            >
              *
            </Typography>
          )}
          {infoText && <InfoIcon text={infoText} />}
        </div>
      )}
      <div
        className={clsx(
          classes.mainContainer,
          required && helperText && classes.mainContainerHasError,
        )}
      >
        {isViewMode && titleText && (
          <div className={classes.viewLabel}>{titleText}</div>
        )}
        {!isViewMode && uploadedFiles.length < maxFiles && (
          <Dropzone
            maxFiles={maxFiles}
            maxSize={maxSize && maxSize * 1024 * 1024}
            accept={allowedFileTypes?.map((t) => "." + t).join(",")}
            noClick={true}
            onDrop={onDropHandler}
            ref={dropzoneRef}
          >
            {({ getRootProps, getInputProps }) => (
              <div
                {...getRootProps({
                  className: classes.dropzone,
                })}
              >
                {loading ? (
                  <div className={classes.setLoading}>
                    <CircularProgress size={20} />
                  </div>
                ) : (
                  <>
                    <input
                      {...getInputProps({
                        multiple: true,
                        accept: allowedFileTypes?.map((t) => "." + t).join(","),
                      })}
                    />
                    <UploadFileContent
                      browse={browse}
                      allowedFileTypes={allowedFileTypes}
                      maxSize={maxSize}
                      maxFiles={maxFiles}
                      subText={subText}
                    />
                  </>
                )}
              </div>
            )}
          </Dropzone>
        )}

        {uploadedFiles.length > 0 &&
          [...uploadedFiles]
            .reverse()
            .map((file) => (
              <PreviewFile
                key={file.id}
                file={file}
                removeFile={removeFile}
                isViewMode={isViewMode}
              />
            ))}
        {!uploadedFiles.length && isViewMode && (
          <PreviewFile isViewMode={isViewMode} />
        )}
      </div>
      {helperText && <div className={classes.helperText}>{helperText}</div>}
    </div>
  );
}

export default React.memo(UploadFile);
